import {useLocalStorage} from '@aboutyou/storage';
import {useEffect} from 'react';
import {useGetOldStoredSearchTerms} from './useGetOldStoredSearchTerms';

export const STORAGE_SEARCHES_KEY = 'tadarida_STORAGE_SEARCHES_KEY';
const STORAGE_SEARCHES_KEY_V2 = 'tadarida_STORAGE_SEARCHES_KEY_V2';
const MAX_STORED_HISTORY_TERMS = 6;

type StoredSearches = {
  terms: Array<{term: string; url: string}>;
};

// The search suggestion now use the resolveURL coming from the TDR GetSuggestions endpoint as a target url (https://aboutyou.atlassian.net/browse/ASFE-9103).
// For backwards compatibility and to extend this logic to the stored search term as well, we have to change the shape of the stored data.
// Hence, a new local storage key, tadarida_STORAGE_SEARCHES_KEY_V2, has been introduced to store user search terms along with the resolve URL.
// This migration logic allows to migrate the old stored search terms from the tadarida_STORAGE_SEARCHES_KEY to the new one (tadarida_STORAGE_SEARCHES_KEY_V2).
// This logic could be removed in 1-2 months (~20 December 2023).
const useMigrateOldStoredSearchTerms = () => {
  const [oldHistorySearchTerm, setOldSearchTermToHistory] =
    useGetOldStoredSearchTerms();

  const [historySearchTerm, setHistorySearchTerm] =
    useLocalStorage<StoredSearches>({
      key: STORAGE_SEARCHES_KEY_V2,
      defaultValue: {terms: []},
    });

  useEffect(() => {
    if (!oldHistorySearchTerm || oldHistorySearchTerm.terms.length === 0) {
      return;
    }

    const mergedTerms = [...(historySearchTerm?.terms || [])];
    for (const oldTerm of oldHistorySearchTerm.terms) {
      if (!mergedTerms.find(term => term.term === oldTerm)) {
        mergedTerms.push({term: oldTerm, url: ''});
      }
    }

    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setHistorySearchTerm({terms: mergedTerms});
    setOldSearchTermToHistory(undefined);
  }, []);
};

export const useStoredSearchTerms = ({
  limitSearchHistory,
}: {
  limitSearchHistory?: boolean;
}) => {
  const [historySearchTerm, addSearchTermToHistory] =
    useLocalStorage<StoredSearches>({
      key: STORAGE_SEARCHES_KEY_V2,
      defaultValue: {terms: []},
    });

  useMigrateOldStoredSearchTerms();

  const handleAddSearchTerm = ({
    term,
    url = '',
  }: {
    term: string;
    url?: string;
  }) => {
    if (
      !historySearchTerm?.terms ||
      historySearchTerm.terms.find(item => item.term === term)
    ) {
      return;
    }

    const limitedHistorySearchTerm = limitSearchHistory
      ? historySearchTerm?.terms.slice(0, MAX_STORED_HISTORY_TERMS)
      : historySearchTerm?.terms;

    addSearchTermToHistory({
      terms: [{term, url}, ...(limitedHistorySearchTerm || [])],
    });
  };

  const clearHistory = () => {
    addSearchTermToHistory({terms: []});
  };

  return {
    recentTerms: historySearchTerm?.terms || [],
    addSearchTerm: handleAddSearchTerm,
    clearHistory,
  };
};
